<template>
  <div class="tab-content" v-show="store.activeTab === 'team'">
  <div class="team-tab">
    <p class="description">Hire personnel to expand your data operations</p>
    
    <div class="personnel-grid">
      <div 
        v-for="person in availablePersons" 
        :key="person.id"
        class="personnel-card"
        :class="{ 'upgrade-required': !person.canBeBuilt(store.state) }"
      >
        <div class="personnel-header">
          <h3>{{ person.name }}</h3>
          <div class="personnel-cost">${{ formatNumber(person.cost) }}</div>
        </div>
        
        <div class="personnel-stats">
          <div v-if="person.produces?.cleanData" class="stat">
            <span class="label">Produces:</span>
            <span class="value positive">{{ formatNumber(person.produces.cleanData) }} clean data/s</span>
          </div>
          <div v-if="person.produces?.rawData" class="stat">
            <span class="label">Produces:</span>
            <span class="value positive">{{ formatNumber(person.produces.rawData) }} raw data/s</span>
          </div>
          <div class="stat">
            <span class="label">Expense:</span>
            <span class="value negative">${{ formatCurrency((person.consumes?.money || 0)) }}/s</span>
          </div>
          <div v-if="person.consumes?.cleanData" class="stat">
            <span class="label">Consumes:</span>
            <span class="value">{{ formatNumber(person.consumes.cleanData) }} clean data/s</span>
          </div>
          <div v-if="person.consumes?.rawData" class="stat">
            <span class="label">Consumes:</span>
            <span class="value">{{ formatNumber(person.consumes.rawData) }} raw data/s</span>
          </div>
        </div>
        
        <div class="personnel-description">
          {{ person.description }}
        </div>
        
        <button 
          class="hire-btn"
          :disabled="!person.canBeBuilt(store.state) || !person.canAfford((store.state.resources.money || 0))"
          @click="hirePerson(person.id)"
          :title="getHireButtonTooltip(person)"
        >
          {{ getHireButtonText(person) }}
        </button>
      </div>
    </div>
    
    <div v-if="availablePersons.length === 0" class="no-personnel">
      <p>No personnel available yet. Purchase the Personnel Management upgrade to unlock hiring.</p>
    </div>
  </div>
</div>

</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useGameStore } from '@/stores/gameStore';
import { formatNumber, formatCurrency } from '@/utils/gameUtils';
import type { PersonnelBundle } from '@/types/EntityTypes';

const store = useGameStore();

const availablePersons = computed(() => store.entityManager.getAvailablePersons(store.state));

function hirePerson(personId: string) {
  const person = store.entityManager.getPersonById(personId);
  if (person) {
    store.hirePerson(personId, personId as keyof PersonnelBundle);
  }
}

function getHireButtonText(person: any): string {
  if (!person.canBeBuilt(store.state)) {
    return 'Upgrade Required';
  }
  if (!person.canAfford(store.state.resources.money)) {
    return 'Not Enough Money';
  }
  return `Hire ${person.name}`;
}

function getHireButtonTooltip(person: any): string {
  if (!person.canBeBuilt(store.state)) {
    return `Requires ${person.upgradeRequired} upgrade`;
  }
  if (!person.canAfford(store.state.resources.money)) {
    return `Need $${formatNumber(person.cost - (store.state.resources.money || 0))} more`;
  }
  return `Hire ${person.name} for $${formatNumber(person.cost)}`;
}
</script>

<style scoped>
.team-tab {
  padding: 20px;
}

.description {
  color: #ccc;
  margin-bottom: 20px;
  font-style: italic;
  padding: 0;
}

.personnel-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.personnel-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-radius: 8px;
  padding: 20px;
  transition: all 0.3s ease;
}

.personnel-card:hover {
  border-color: #666;
  background: rgba(255, 255, 255, 0.08);
}

.personnel-card.upgrade-required {
  opacity: 0.6;
  border-color: #666;
}

.personnel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.personnel-header h3 {
  margin: 0;
  color: #00ff88;
  font-size: 1.2em;
}

.personnel-cost {
  background: #333;
  padding: 5px 10px;
  border-radius: 4px;
  font-weight: bold;
  color: #ffaa00;
}

.personnel-stats {
  margin-bottom: 15px;
}

.stat {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-size: 0.9em;
}

.label {
  color: #ccc;
}

.value {
  font-weight: bold;
}

.value.positive {
  color: #00ff88;
}

.value.negative {
  color: #ff4444;
}

.personnel-description {
  color: #aaa;
  font-size: 0.9em;
  margin-bottom: 15px;
  line-height: 1.4;
}

.hire-btn {
  width: 100%;
  padding: 10px;
  background: #00ff88;
  color: #000;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
}

.hire-btn:hover:not(:disabled) {
  background: #00cc6a;
  transform: translateY(-1px);
}

.hire-btn:disabled {
  background: #666;
  color: #999;
  cursor: not-allowed;
}

.no-personnel {
  text-align: center;
  color: #ccc;
  font-style: italic;
  margin-top: 50px;
}

@media (max-width: 768px) {
  .personnel-grid {
    grid-template-columns: 1fr;
  }
}
</style> 
